import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import api, { formatPrice, formatKm, formatApiError } from "@/lib/api";
import { Plus, Edit2, Trash2, LogOut, MessageSquare, Car, Sparkles, Download, X, RefreshCw, Plane } from "lucide-react";
import { toast } from "sonner";
import VehicleForm from "@/pages/AdminVehicleForm";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [editing, setEditing] = useState(null); // vehicle object or 'new' or null
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  const loadVehicles = () => api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  const loadInquiries = () => api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {});

  useEffect(() => {
    if (user) {
      loadVehicles();
      loadInquiries();
    }
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleDelete = async (id) => {
    if (!window.confirm("Fahrzeug wirklich löschen?")) return;
    try {
      await api.delete(`/admin/vehicles/${id}`);
      toast.success("Fahrzeug gelöscht");
      loadVehicles();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleImport = async () => {
    const url = importUrl.trim();
    if (!url) { toast.error("Bitte Willhaben-Link eingeben"); return; }
    setImporting(true);
    try {
      const res = await api.post("/admin/willhaben/import", { url }, { timeout: 120000 });
      toast.success(`Import erfolgreich: ${res.data.imported_images} von ${res.data.attempted_images} Bildern übernommen`);
      setImportUrl("");
      setShowImport(false);
      loadVehicles();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm("Anfrage löschen?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Anfrage gelöscht");
      loadInquiries();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (editing !== null) {
    return (
      <VehicleForm
        vehicle={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); loadVehicles(); }}
      />
    );
  }

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-80px)]" data-testid="admin-dashboard">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Admin-Bereich</div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">D.R Automobile · Verwaltung</h1>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-end">
            <Link
              to="/admin/betriebsurlaub"
              data-testid="open-vacation"
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 px-3 md:px-4 py-2 text-sm font-medium hover:border-slate-900 hover:text-slate-900 transition-crisp"
            >
              <Plane size={14} /> <span className="hidden sm:inline">Betriebsurlaub</span><span className="sm:hidden">Urlaub</span>
            </Link>
            <Link
              to="/admin/foto-studio"
              data-testid="open-photo-studio"
              className="inline-flex items-center gap-2 border border-amber-500 text-amber-700 px-3 md:px-4 py-2 text-sm font-medium hover:bg-amber-500 hover:text-white transition-crisp"
            >
              <Sparkles size={14} /> Foto-Studio
            </Link>
            <span className="text-sm text-slate-600 hidden sm:inline" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm border border-slate-300 px-4 py-2 hover:bg-slate-900 hover:text-white transition-crisp">
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex gap-1">
          <TabBtn active={tab === "vehicles"} onClick={() => setTab("vehicles")} icon={Car} label={`Fahrzeuge (${vehicles.length})`} testId="tab-vehicles" />
          <TabBtn active={tab === "inquiries"} onClick={() => setTab("inquiries")} icon={MessageSquare} label={`Anfragen (${inquiries.length})`} testId="tab-inquiries" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        {tab === "vehicles" ? (
          <>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <h2 className="font-heading font-semibold text-lg text-slate-900">Alle Fahrzeuge</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setShowImport(true)}
                  data-testid="willhaben-import-btn"
                  className="flex items-center gap-2 border border-slate-900 text-slate-900 px-4 py-2.5 text-sm font-medium hover:bg-slate-900 hover:text-white transition-crisp"
                >
                  <Download size={14} /> Willhaben importieren
                </button>
                <button onClick={() => setEditing("new")} data-testid="add-vehicle-btn" className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-red-600 transition-crisp">
                  <Plus size={16} /> Fahrzeug hinzufügen
                </button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Fahrzeug</th>
                    <th className="text-left px-4 py-3">Baujahr</th>
                    <th className="text-left px-4 py-3">KM</th>
                    <th className="text-left px-4 py-3">Preis</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500">Keine Fahrzeuge.</td></tr>}
                  {vehicles.map((v) => (
                    <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-vehicle-${v.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{v.make} {v.model}</div>
                        <div className="text-xs text-slate-500">{v.fuel_type} · {v.transmission}</div>
                      </td>
                      <td className="px-4 py-3">{v.year}</td>
                      <td className="px-4 py-3">{formatKm(v.mileage)}</td>
                      <td className="px-4 py-3 font-medium">{formatPrice(v.price)}</td>
                      <td className="px-4 py-3">
                        {v.sold ? <span className="bg-red-600 text-white px-2 py-0.5 text-[10px] uppercase">Verkauft</span>
                          : v.featured ? <span className="bg-slate-900 text-white px-2 py-0.5 text-[10px] uppercase">Empfohlen</span>
                            : <span className="text-slate-500 text-xs">Aktiv</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <Link to={`/fahrzeuge/${v.id}`} target="_blank" className="text-xs text-slate-600 hover:text-slate-900 underline">Vorschau</Link>
                          <button onClick={() => setEditing(v)} data-testid={`edit-${v.id}`} className="p-1.5 hover:bg-slate-200" title="Bearbeiten"><Edit2 size={14} /></button>
                          <button onClick={() => handleDelete(v.id)} data-testid={`delete-${v.id}`} className="p-1.5 hover:bg-red-600 hover:text-white" title="Löschen"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div data-testid="inquiries-list">
            <h2 className="font-heading font-semibold text-lg text-slate-900 mb-6">Eingegangene Anfragen</h2>
            {inquiries.length === 0 ? (
              <div className="bg-white border border-slate-200 p-12 text-center text-slate-500">Noch keine Anfragen.</div>
            ) : (
              <div className="space-y-4">
                {inquiries.map((q) => {
                  const linkedVehicle = vehicles.find((v) => v.id === q.vehicle_id);
                  return (
                    <div key={q.id} className="bg-white border border-slate-200 p-5" data-testid={`inquiry-${q.id}`}>
                      <div className="flex justify-between items-start gap-4 flex-wrap">
                        <div>
                          <div className="font-medium text-slate-900">{q.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            <a href={`mailto:${q.email}`} className="hover:text-slate-900">{q.email}</a>
                            {q.phone && <> · <a href={`tel:${q.phone}`} className="hover:text-slate-900">{q.phone}</a></>}
                          </div>
                          {linkedVehicle && (
                            <Link to={`/fahrzeuge/${q.vehicle_id}`} target="_blank" className="text-xs text-red-600 underline mt-1 inline-block">
                              → {linkedVehicle.make} {linkedVehicle.model}
                            </Link>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">{new Date(q.created_at).toLocaleString("de-AT")}</span>
                          <button onClick={() => handleDeleteInquiry(q.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-slate-700 whitespace-pre-line border-t border-slate-100 pt-3">{q.message}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Willhaben Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70" data-testid="willhaben-modal">
          <div className="bg-white w-full max-w-lg p-6 md:p-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">
                  <span className="divider-thin" />Import
                </div>
                <h2 className="font-heading font-bold text-xl text-slate-900">Willhaben-Anzeige importieren</h2>
              </div>
              <button onClick={() => { setShowImport(false); setImportUrl(""); }} className="text-slate-400 hover:text-slate-900" data-testid="willhaben-close">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Füge den Link zur einzelnen Willhaben-Anzeige (Auto-Inserat) ein — die Daten und Bilder werden übernommen und als neues Fahrzeug angelegt.
            </p>
            <input
              type="url"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://www.willhaben.at/iad/gebrauchtwagen/…"
              data-testid="willhaben-url"
              disabled={importing}
              className="w-full border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-slate-900 mb-4 disabled:bg-slate-100"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowImport(false); setImportUrl(""); }}
                disabled={importing}
                className="border border-slate-300 px-5 py-2.5 text-sm hover:bg-slate-100 transition-crisp"
              >
                Abbrechen
              </button>
              <button
                onClick={handleImport}
                disabled={importing}
                data-testid="willhaben-submit"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-red-600 transition-crisp disabled:opacity-50"
              >
                {importing ? (<><RefreshCw size={14} className="animate-spin" /> Importiere …</>) : (<><Download size={14} /> Importieren</>)}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-4">
              Hinweis: Es werden nur öffentlich einsehbare Daten der Anzeige übernommen. Bilder werden lokal in unserer Datenbank gespeichert.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-crisp ${
        active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
      }`}>
      <Icon size={15} /> {label}
    </button>
  );
}
